import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useUserStore, useAuthStore } from '@/stores'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, User, Calendar, Clock, Edit, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Identicon } from '@/components/Identicon'
import { Skeleton } from '@/components/ui/skeleton'
import { AdminRole } from '@/types'
import type { User as UserType } from '@/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

const UserDetail = observer(() => {
  const { tenantId, userId } = useParams<{ tenantId: string; userId: string }>()
  const navigate = useNavigate()
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [editUser, setEditUser] = useState({
    email: '',
    name: '',
    role: AdminRole,
    password: ''
  })

  useEffect(() => {
    if (tenantId && userId) {
      userStore.getUser(tenantId, userId)
    }
  }, [tenantId, userId, userStore])

  const user = userStore.currentUser

  useEffect(() => {
    if (user) {
      setEditUser({
        email: user.email,
        name: user.name || '',
        role: user.role,
        password: ''
      })
    }
  }, [user])

  const handleBack = () => {
    navigate(`/tenants/${tenantId}/users`)
  }

  const handleEdit = () => {
    setIsEditDialogOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!tenantId || !userId) return

    const updateData: Pick<UserType, 'email' | 'name' | 'role'> & { password?: string } = {
      email: editUser.email,
      name: editUser.name,
      role: editUser.role
    }

    if (editUser.password) {
      updateData.password = editUser.password
    }

    const success = await userStore.updateUser(tenantId, userId, updateData)
    if (success) {
      setIsEditDialogOpen(false)
      await userStore.getUser(tenantId, userId)
    }
  }

  const handleDelete = async () => {
    if (!tenantId || !userId) return

    const success = await userStore.deleteUser(tenantId, userId)
    if (success) {
      navigate(`/tenants/${tenantId}/users`)
    }
  }

  const getRoleDisplay = (role: number) => {
    return role === AdminRole ? '管理者' : 'ユーザー'
  }

  const isCurrentUser = user?.id === authStore.user?.id

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-[200px]" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          戻る
        </Button>
        {!isCurrentUser && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
            >
              <Edit className="h-4 w-4 mr-2" />
              編集
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              削除
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            ユーザー詳細
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              <Identicon source={user.email} size={80} />
            </div>
            
            <div className="flex-1 grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">ID</div>
                  <div className="font-mono text-sm">{user.id}</div>
                </div>
                
                <div>
                  <div className="text-sm text-muted-foreground mb-1">名前</div>
                  <div className="font-medium">{user.name || '-'}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">メールアドレス</div>
                  <div>{user.email}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">ロール</div>
                  <Badge variant="outline">
                    {getRoleDisplay(user.role)}
                  </Badge>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">テナントID</div>
                  <div className="font-mono text-sm">{user.tenant}</div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    登録日時
                  </div>
                  <div>
                    {user.created 
                      ? new Date(user.created).toLocaleString('ja-JP')
                      : '-'}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    更新日時
                  </div>
                  <div>
                    {user.updated 
                      ? new Date(user.updated).toLocaleString('ja-JP')
                      : '-'}
                  </div>
                </div>

                {isCurrentUser && (
                  <div>
                    <Badge variant="secondary">現在のユーザー</Badge>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>ユーザー編集</DialogTitle>
            <DialogDescription>
              ユーザー情報を編集します。
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-email">メールアドレス</Label>
              <Input
                id="edit-email"
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                placeholder="user@example.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-name">名前</Label>
              <Input
                id="edit-name"
                value={editUser.name}
                onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                placeholder="山田 太郎"
              />
            </div>
            <div>
              <Label htmlFor="edit-role">ロール</Label>
              <Select 
                value={editUser.role.toString()} 
                onValueChange={(value) => setEditUser({ ...editUser, role: parseInt(value) })}
              >
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AdminRole.toString()}>管理者</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit-password">新しいパスワード（変更する場合のみ）</Label>
              <Input
                id="edit-password"
                type="password"
                value={editUser.password}
                onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                placeholder="8文字以上"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              キャンセル
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={
                !editUser.email || editUser.password?.length < 8
              }
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ユーザーの削除</AlertDialogTitle>
            <AlertDialogDescription>
              このユーザーを削除してもよろしいですか？この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>
              削除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
})

export default UserDetail